import type { AsyncDataOptions } from '#app'
import type { NuxtLocale } from '~/constants/locales'
export type KeysOf<T> = Array<
  T extends T // Include all keys of union types, not just common keys
    ? keyof T extends string
      ? keyof T
      : never
    : never
>

let fetchedLocale: undefined | NuxtLocale = undefined

export default async function useCachedAsyncData<
  ResT,
  NuxtErrorDataT = unknown,
  DataT = ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = undefined,
>(
  key: string,
  handler: () => Promise<ResT>,
  options?: Omit<
    AsyncDataOptions<ResT, DataT, PickKeys, DefaultT>,
    'getCachedData'
  >,
  expirationTime: number = 10 * 60 * 1000
) {
  const nuxtApp = useNuxtApp()
  const { setFetchedTime, getFetchedTime } = useCacheKeysStore()
  const fetchedTime = getFetchedTime(key) ?? new Date()
  const { locale } = useI18n()

  const {
    data,
    status,
    error,
    refresh: forceRefresh,
    clear,
    execute,
  } = await useAsyncData<ResT, NuxtErrorDataT, DataT, PickKeys, DefaultT>(
    key,
    handler,
    {
      ...options,
      transform: async (_result) => {
        setFetchedTime(key, new Date())
        fetchedLocale = locale.value

        let result = _result as unknown as DataT

        if (options?.transform) {
          result = await options.transform(_result)
        }

        return result
      },
      getCachedData: (key) => {
        const cachedData = (nuxtApp.payload.data[key] ||
          nuxtApp.static.data[key]) as DataT | undefined
        if (!cachedData || isExpired(fetchedTime, fetchedLocale)) {
          return
        }
        return cachedData
      },
    }
  )

  function refresh() {
    if (!isExpired(fetchedTime, fetchedLocale)) {
      return
    } else {
      forceRefresh()
    }
  }

  function isExpired(fetchedAt: Date, fetchedLocale?: string) {
    const expirationDate = new Date(fetchedAt)
    expirationDate.setTime(expirationDate.getTime() + expirationTime)
    return expirationDate < new Date() || fetchedLocale !== locale.value
  }

  return { data, status, refresh, error, clear, execute }
}
